import { IndividualPlanTier, OrganizationPlanTier, SubscriptionPlanTier } from '@ftr/external-organization-contracts'
import { BadgeColors, Color, isInDesktopApp } from '@ftr/foundation'
import { HelpSubMenuItem } from '../help-sub-menu-flyout/item/help-sub-menu-flyout-item.component'

export const HelpCenterItem: HelpSubMenuItem = {
  title: 'Help Center',
  description: 'Access knowledge base articles for FTR Justice Cloud',
  icon: 'HelpCircleLine',
  iconColor: BadgeColors.Blueberry,
  badgeColor: Color.Blueberry10,
  visible: true,
  isElevio: true,
}

export const HelpCenterPublicItem: HelpSubMenuItem = {
  ...HelpCenterItem,
  elevioArticleId: 'TODO',
}

export const SUPPORT_PORTAL_LINK = 'https://ftrgroup.service-now.com/customer'

export const SupportPortalItem: HelpSubMenuItem = {
  title: 'Support Portal',
  description: 'Submit and track requests for technical support',
  icon: 'AuditLog',
  iconColor: BadgeColors.Grape,
  badgeColor: Color.Grape10,
  link: SUPPORT_PORTAL_LINK,
  visible: true,
}

export const ProductUpdatesItem: HelpSubMenuItem = {
  title: 'Product Updates',
  description: 'The latest release notes for FTR Justice Cloud',
  icon: 'ErrorCircleLine',
  iconColor: BadgeColors.Mint,
  badgeColor: Color.Mint10,
  link: 'https://fortherecord.releasenotes.io',
  visible: true,
}

export const SystemStatusItem: HelpSubMenuItem = {
  title: 'System Status',
  description: 'Check the status of FTR Justice Cloud and subscribe to incident updates',
  icon: 'CheckmarkCircleLine',
  iconColor: BadgeColors.Apple,
  badgeColor: Color.Apple10,
  link: 'https://fortherecord.statuspage.io',
  visible: true,
}

export const TrustCenterItem: HelpSubMenuItem = {
  title: 'Trust Center',
  description: "Public access to FTR Justice Cloud's compliance and security posture",
  icon: 'VerifyLine',
  iconColor: BadgeColors.Lemon,
  badgeColor: Color.Lemon10,
  link: 'https://trust.fortherecord.com',
  visible: true,
}

export const CommunityForumItem: HelpSubMenuItem = {
  title: 'Community Forum',
  description: 'The home for discussion about all For The Record products',
  icon: 'Forum',
  iconColor: BadgeColors.Watermelon,
  badgeColor: Color.Watermelon10,
  link: 'https://fortherecord.discourse.group',
  visible: true,
}

export function desktopAppsItem(desktopAppDownloadUrl: string): HelpSubMenuItem {
  return {
    title: 'Desktop Apps',
    description: 'Download the latest FTR Justice Cloud apps for performance and connectivity.',
    icon: 'Remote',
    iconColor: BadgeColors.Mandarin,
    badgeColor: Color.Mandarin10,
    link: desktopAppDownloadUrl,
    visible: true,
  }
}

export function publicPortalItem(publicPortalUrl: string): HelpSubMenuItem {
  return {
    title: 'Public Portal',
    description: 'Request transcripts or recordings.',
    icon: 'PeopleLine',
    iconColor: BadgeColors.Blueberry,
    badgeColor: Color.Blueberry10,
    link: publicPortalUrl,
    visible: true,
  }
}

export function courtPortalItem(courtsAppUrl: string): HelpSubMenuItem {
  return {
    title: 'Court Portal',
    description: 'View and manage court content. For authorized court staff only.',
    icon: 'CourthouseLine',
    iconColor: BadgeColors.Mint,
    badgeColor: Color.Mint10,
    link: courtsAppUrl,
    visible: true,
  }
}

export function getCourtsHelpSupport(params: {
  desktopAppDownloadUrl: string
  isFtrForumEnabled: boolean
  hasCourtSystems: boolean
  publicPortalUrl?: string
}): HelpSubMenuItem[] {
  const items: HelpSubMenuItem[] = [
    HelpCenterItem,
    SupportPortalItem,
    ProductUpdatesItem,
    SystemStatusItem,
    TrustCenterItem,
  ]
  if (!isInDesktopApp()) {
    items.push(desktopAppsItem(params.desktopAppDownloadUrl))
  }
  if (params.isFtrForumEnabled && params.hasCourtSystems) {
    items.push(CommunityForumItem)
  }
  if (params.publicPortalUrl) {
    items.push(publicPortalItem(params.publicPortalUrl))
  }
  return items
}

export function getPublicPortalHelpSupport(params: {
  desktopAppDownloadUrl: string
  isFtrForumEnabled: boolean
  courtsAppUrl: string
  isLoggedIn: boolean
  planTier?: SubscriptionPlanTier
}): HelpSubMenuItem[] {
  // Logged out and Free Individual Tiers return the same results
  const subscriptionTier = !params.isLoggedIn || !params.planTier ? IndividualPlanTier.Free : params.planTier
  const desktopApps = !isInDesktopApp() ? [desktopAppsItem(params.desktopAppDownloadUrl)] : []
  const communityForum = params.isFtrForumEnabled ? [CommunityForumItem] : []
  const courtPortal = [courtPortalItem(params.courtsAppUrl)]

  switch (subscriptionTier) {
    case IndividualPlanTier.Free:
      return [HelpCenterPublicItem, ProductUpdatesItem, SystemStatusItem, ...desktopApps, ...courtPortal]
    case OrganizationPlanTier.Enterprise:
      return [
        HelpCenterPublicItem,
        SupportPortalItem,
        ProductUpdatesItem,
        SystemStatusItem,
        TrustCenterItem,
        ...desktopApps,
        ...communityForum,
        ...courtPortal,
      ]
    default:
      // Fallback to default: Logged In + Essentials, Advanced, Premium, Professional Tier
      return [
        HelpCenterPublicItem,
        SupportPortalItem,
        ProductUpdatesItem,
        SystemStatusItem,
        ...desktopApps,
        ...communityForum,
        ...courtPortal,
      ]
  }
}

export function getLoggedOutTopNavHelpSupport(params: { desktopAppDownloadUrl: string }): HelpSubMenuItem[] {
  const items: HelpSubMenuItem[] = [HelpCenterPublicItem, ProductUpdatesItem, SystemStatusItem]
  if (!isInDesktopApp()) {
    items.push(desktopAppsItem(params.desktopAppDownloadUrl))
  }
  return items
}

// Used for both the top navigation and the side navigation when logged out.
export function getLoggedOutCourtsHelpSupport(params: {
  desktopAppDownloadUrl: string
  publicPortalUrl?: string
  isFtrForumEnabled?: boolean
}): HelpSubMenuItem[] {
  const items: HelpSubMenuItem[] = [HelpCenterItem, SupportPortalItem, ProductUpdatesItem, SystemStatusItem]
  if (!isInDesktopApp()) {
    items.push(desktopAppsItem(params.desktopAppDownloadUrl))
  }
  if (params.publicPortalUrl) {
    items.push(publicPortalItem(params.publicPortalUrl))
  }
  return items
}
